/**
 * Auth-domain typed signal error classes.
 *
 * Each class corresponds to one failure mode previously expressed as a
 * string `AuthErrorCode`. Routing is registered centrally so every error
 * flows to the correct sinks (console, Axiom, Sentry) automatically.
 *
 * NOTE on `no_session`: @imible/common-signals exports `NotAuthenticated`
 * which covers the same concept. We define a local `NoSession` here to avoid
 * adding @imible/common-signals as a dependency — auth is a low-level package
 * and that dep is not yet wired. When common-signals is added as a dep, replace
 * `NoSession` with a re-export of `NotAuthenticated`.
 */

import { Signal, SignalError, registerSignalRouting } from "@mfg/result";

// ─── Routing registration ────────────────────────────────────────────────────

registerSignalRouting({
  NoSession: {
    severity: "warn",
    streams: ["console"],
    i18nKey: "auth.error.noSession",
  },
  InvalidCredentials: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.invalidCredentials",
  },
  UserAlreadyExists: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.userAlreadyExists",
  },
  PasswordError: {
    severity: "warn",
    streams: ["console"],
    i18nKey: "auth.error.passwordError",
  },
  AuthNetworkError: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.network",
  },
  TokenExpired: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.tokenExpired",
  },
  AuthConfigError: {
    severity: "error",
    streams: ["console", "stdout-json", "axiom"],
    i18nKey: "auth.error.configError",
  },
  AuthClientError: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.clientError",
  },
  AuthServerError: {
    severity: "error",
    streams: ["console", "stdout-json", "axiom"],
    i18nKey: "auth.error.serverError",
  },
  AuthOperationFailed: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.operationFailed",
  },
  AuthUnexpectedError: {
    severity: "error",
    streams: ["console", "stdout-json", "axiom"],
    i18nKey: "auth.error.unexpected",
  },
  InvalidForm: {
    severity: "warn",
    streams: ["console"],
    i18nKey: "auth.error.invalidForm",
  },
  TurnstileFailed: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.turnstileFailed",
  },
  OtpFailed: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.otpFailed",
  },
  AuthUpdateFailed: {
    severity: "warn",
    streams: ["console", "axiom"],
    i18nKey: "auth.error.updateFailed",
  },
  // SCAFFOLDED — call sites not yet migrated. See TASK-G mass migration.
  ConfigMissing: {
    severity: "error",
    streams: ["console", "stdout-json", "sentry", "axiom"],
    i18nKey: "common.error.configMissing",
  },
  LoginFlowException: {
    severity: "error",
    streams: ["console", "stdout-json", "sentry", "axiom"],
    piiFields: ["email"],
    i18nKey: "auth.error.loginFlowException",
  },
  SignupFlowException: {
    severity: "error",
    streams: ["console", "stdout-json", "sentry", "axiom"],
    piiFields: ["email"],
    i18nKey: "auth.error.signupFlowException",
  },
  SignupAttempted: {
    severity: "info",
    streams: ["axiom"],
  },
  LoginSucceeded: {
    severity: "info",
    streams: ["axiom"],
    piiFields: ["email"],
  },
  SignupSucceeded: {
    severity: "info",
    streams: ["axiom"],
    piiFields: ["email"],
  },
  LogoutSucceeded: {
    severity: "info",
    streams: ["axiom"],
  },
  LogoutFailed: {
    severity: "error",
    streams: ["console", "stdout-json", "sentry", "axiom"],
    i18nKey: "auth.error.logoutFailed",
  },
});

// ─── Signal error classes ────────────────────────────────────────────────────

/**
 * No authenticated session exists for the requested operation.
 *
 * Mirrors @imible/common-signals `NotAuthenticated` — replace with that
 * once @imible/common-signals is added as a dep of @imible/auth.
 */
export class NoSession extends SignalError<"NoSession"> {
  constructor(
    public readonly operation: string,
    options?: ErrorOptions,
  ) {
    super(`No session: ${operation}`, options);
  }
  payload(): Record<string, unknown> {
    return { operation: this.operation };
  }
}

/** Email/password sign-in failed because credentials are wrong. */
export class InvalidCredentials extends SignalError<"InvalidCredentials"> {
  constructor(options?: ErrorOptions) {
    super("Invalid login credentials", options);
  }
  payload(): Record<string, unknown> {
    return {};
  }
}

/** Sign-up failed because an account with this email already exists. */
export class UserAlreadyExists extends SignalError<"UserAlreadyExists"> {
  constructor(options?: ErrorOptions) {
    super("User already registered", options);
  }
  payload(): Record<string, unknown> {
    return {};
  }
}

/** Sign-up or password change failed due to a password constraint violation. */
export class PasswordError extends SignalError<"PasswordError"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Password error: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Auth call failed because of a network or connectivity problem. */
export class AuthNetworkError extends SignalError<"AuthNetworkError"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Auth network error: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Auth token has expired or been invalidated. */
export class TokenExpired extends SignalError<"TokenExpired"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Token expired: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Auth configuration is missing or invalid (env vars, keys, etc.). */
export class AuthConfigError extends SignalError<"AuthConfigError"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Auth config error: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Auth call was rejected due to a client-side error (4xx, bad request). */
export class AuthClientError extends SignalError<"AuthClientError"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Auth client error: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Auth call failed because of a server-side error (5xx). */
export class AuthServerError extends SignalError<"AuthServerError"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Auth server error: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Generic auth operation failure — use when no more specific class fits. */
export class AuthOperationFailed extends SignalError<"AuthOperationFailed"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Auth operation failed: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** An unexpected / unclassified error was caught in an auth operation. */
export class AuthUnexpectedError extends SignalError<"AuthUnexpectedError"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Unexpected auth error: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Form data failed validation before any auth call was attempted. */
export class InvalidForm extends SignalError<"InvalidForm"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Invalid form: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Cloudflare Turnstile challenge verification failed. */
export class TurnstileFailed extends SignalError<"TurnstileFailed"> {
  constructor(options?: ErrorOptions) {
    super("Turnstile verification failed", options);
  }
  payload(): Record<string, unknown> {
    return {};
  }
}

/** OTP verification failed (wrong code, expired, etc.). */
export class OtpFailed extends SignalError<"OtpFailed"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`OTP verification failed: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

/** Supabase `updateUser` call failed. */
export class AuthUpdateFailed extends SignalError<"AuthUpdateFailed"> {
  constructor(
    public readonly detail: string,
    options?: ErrorOptions,
  ) {
    super(`Auth update failed: ${detail}`, options);
  }
  payload(): Record<string, unknown> {
    return { detail: this.detail };
  }
}

// SCAFFOLDED — call sites not yet migrated. See TASK-G mass migration.

/** Required configuration (env vars, keys) missing — usually Supabase. */
export class ConfigMissing extends SignalError<"ConfigMissing"> {
  constructor(
    public readonly keyName: string,
    options?: ErrorOptions,
  ) {
    super(`Config missing: ${keyName}`, options);
  }
  payload(): Record<string, unknown> {
    return { keyName: this.keyName };
  }
}

/** Uncaught throw in `login()` catch block — escalates to error severity. */
export class LoginFlowException extends SignalError<"LoginFlowException"> {
  constructor(
    public readonly email: string,
    public readonly reason: string,
    options?: ErrorOptions,
  ) {
    super(`Login flow exception for ${email}: ${reason}`, options);
  }
  payload(): Record<string, unknown> {
    return { email: this.email, reason: this.reason };
  }
}

/** Uncaught throw in `signup()` catch block — escalates to error severity. */
export class SignupFlowException extends SignalError<"SignupFlowException"> {
  constructor(
    public readonly email: string,
    public readonly reason: string,
    options?: ErrorOptions,
  ) {
    super(`Signup flow exception for ${email}: ${reason}`, options);
  }
  payload(): Record<string, unknown> {
    return { email: this.email, reason: this.reason };
  }
}

/** Mirror event for legacy `logger.event("user.signup")`. */
export class SignupAttempted extends Signal<"SignupAttempted"> {
  constructor(public readonly source: string) {
    super();
  }
  payload(): Record<string, unknown> {
    return { source: this.source };
  }
}

/** Replaces `logger.logAuth("Login successful")`. */
export class LoginSucceeded extends Signal<"LoginSucceeded"> {
  constructor(public readonly email: string) {
    super();
  }
  payload(): Record<string, unknown> {
    return { email: this.email };
  }
}

/** Replaces `logger.logAuth("Signup successful")`. */
export class SignupSucceeded extends Signal<"SignupSucceeded"> {
  constructor(public readonly email: string) {
    super();
  }
  payload(): Record<string, unknown> {
    return { email: this.email };
  }
}

/** Replaces `logger.info("[AuthBootstrap] User logged out...")`. */
export class LogoutSucceeded extends Signal<"LogoutSucceeded"> {
  constructor() {
    super();
  }
  payload(): Record<string, unknown> {
    return {};
  }
}

/** Replaces `logger.error("Error during logout:")`. */
export class LogoutFailed extends SignalError<"LogoutFailed"> {
  constructor(
    public readonly reason: string,
    options?: ErrorOptions,
  ) {
    super(`Logout failed: ${reason}`, options);
  }
  payload(): Record<string, unknown> {
    return { reason: this.reason };
  }
}
